import { AnimatePresence, motion } from "motion/react";
import type React from "react";
import { useEffect } from "react";
import { NavLink, useLocation } from "react-router";
import { twJoin } from "tailwind-merge";

import { ChevronIcon } from "@/components/CustomIcons/ChevronIcon";
import { twResolve } from "@/helpers/tw-resolve";
import { useBool } from "@/hooks/useBool";
import { useIsDesktop } from "@/hooks/useIsDesktop";
import { Collapsible as ArkCollapsible, Menu as ArkMenu, Portal as ArkPortal } from "@/ui-adapters/ArkSidebar";
import { RuntimeFloatingPortal } from "@/ui-adapters/RuntimeFloatingPanel";

import { sidebarItemLabelAnimation } from "./animations";
import { SidebarItem, SidebarItemFloatingTooltip, sidebarItemVariants } from "./SidebarItem";
import type { SidebarBaseItemType, SidebarGroupedItemType } from "./types";
import { useSidebarCollapsedTooltip } from "./useSidebarCollapsedTooltip";

interface SidebarGroupedItemProps extends SidebarGroupedItemType {}

function getPathname(to: string | undefined): string | undefined {
  return to?.split(/[?#]/, 1)[0];
}

/** Renders a collapsible sidebar group with an optional directly navigable parent route. */
export function SidebarGroupedItem({ label, icon, items, to, ...props }: SidebarGroupedItemProps): React.ReactNode {
  const [isActive, activityHandlers] = useBool(false);
  const [isExpanded, expandHandlers] = useBool(isActive);
  const [isMenuVisible, menuVisibilityHandlers] = useBool(false);

  const { showTooltip, hideTooltip, referenceRef, panelProps, isTooltipVisible, isCollapsed } =
    useSidebarCollapsedTooltip();
  const location = useLocation();
  const isDesktop = useIsDesktop();

  useEffect(() => {
    const parentPathname = getPathname(to);
    const isAnyItemActive =
      location.pathname === parentPathname ||
      items.some((item) => {
        const itemPathname = getPathname(item.to);

        return itemPathname ? location.pathname.includes(itemPathname) : false;
      });

    activityHandlers.set(isAnyItemActive);
    expandHandlers.set(isAnyItemActive);
    // Use `JSON.stringify` to compare array values rather than reference
    // oxlint-disable-next-line react-hooks/exhaustive-deps
  }, [activityHandlers, expandHandlers, JSON.stringify(items), location.pathname, to]);

  // Reset menu when sidebar is collapsed
  useEffect(() => {
    if (!isCollapsed) {
      return;
    }

    expandHandlers.set(false);
    // oxlint-disable-next-line react-hooks/exhaustive-deps
  }, [isCollapsed]);

  useEffect(() => {
    if (!isMenuVisible || location.pathname === getPathname(to)) {
      return;
    }

    menuVisibilityHandlers.set(false);
    // oxlint-disable-next-line react-hooks/exhaustive-deps
  }, [location.pathname, to]);

  if (items.length === 0) {
    return to ? <SidebarItem {...props} label={label} icon={icon} to={to} exact /> : null;
  }

  if (items.length === 1 && !to) {
    return <SidebarItem {...items[0]} {...{ icon }} key={undefined} />;
  }

  const parentLink = to ? (
    <NavLink
      to={to}
      end
      aria-label={label}
      data-testid={props["data-testid"]}
      ref={referenceRef}
      onMouseEnter={isCollapsed ? showTooltip : undefined}
      onMouseLeave={isCollapsed ? hideTooltip : undefined}
      className={twJoin(
        isCollapsed && sidebarItemVariants({ isActive: isActive || isMenuVisible, isCollapsed: true }),
        !isCollapsed &&
          "flex min-h-control-md min-w-0 flex-1 items-center gap-space-sm rounded-lg p-space-sm hover:bg-action-subtle-primary-hover focus:outline-hidden focus-visible:bg-action-subtle-primary-hover",
      )}
      onClick={() => {
        expandHandlers.set(true);
        props.onClick?.();
      }}
    >
      <span className="flex size-icon-inline shrink-0 items-center justify-center [&_svg]:size-icon-inline">
        {icon}
      </span>
      {!isCollapsed && (
        <motion.span
          className="block truncate font-medium"
          variants={sidebarItemLabelAnimation}
          initial={isDesktop ? "hidden" : "visible"}
          animate="visible"
          exit="hidden"
        >
          {label}
        </motion.span>
      )}
    </NavLink>
  ) : null;

  const linkedHeader = parentLink ? (
    <header
      className={twJoin(
        "flex w-full items-center rounded-lg text-body-emphasis text-button-tone-primary",
        (isActive || isExpanded) && "bg-action-soft-primary",
      )}
    >
      {parentLink}
      <ArkCollapsible.Trigger asChild>
        <button
          type="button"
          aria-label={label}
          data-testid={`${props["data-testid"]}-toggle`}
          className="flex min-h-control-md w-control-md shrink-0 items-center justify-center rounded-lg hover:bg-action-subtle-primary-hover focus:outline-hidden focus-visible:bg-action-subtle-primary-hover"
        >
          <ChevronIcon orientation={isExpanded ? "down" : "right"} />
        </button>
      </ArkCollapsible.Trigger>
    </header>
  ) : null;

  if (isCollapsed) {
    return (
      <li className="w-full">
        <ArkMenu.Root
          positioning={{
            placement: "right",
          }}
          open={isMenuVisible}
          onOpenChange={menuVisibilityHandlers.toggle}
        >
          {parentLink ? (
            <ArkMenu.Trigger asChild>{parentLink}</ArkMenu.Trigger>
          ) : (
            <ArkMenu.Trigger asChild ref={referenceRef}>
              <button
                type="button"
                className={sidebarItemVariants({
                  isActive: isActive || isMenuVisible,
                  isCollapsed: true,
                })}
                data-testid={props["data-testid"]}
                onMouseEnter={isCollapsed ? showTooltip : undefined}
                onMouseLeave={isCollapsed ? hideTooltip : undefined}
                // Required for screenreader when sidebar is collapsed
                aria-label={label}
              >
                <span
                  className={twJoin("flex min-w-0 items-center gap-space-sm", isCollapsed ? undefined : "truncate")}
                >
                  <span className="flex size-icon-inline shrink-0 items-center justify-center [&_svg]:size-icon-inline">
                    {icon}
                  </span>
                  <AnimatePresence>
                    {!isCollapsed && (
                      <motion.span
                        className="block truncate"
                        variants={isDesktop ? sidebarItemLabelAnimation : undefined}
                        initial="hidden"
                        animate="visible"
                        exit="hidden"
                      >
                        {label}
                      </motion.span>
                    )}
                  </AnimatePresence>
                </span>
                {!isCollapsed && <ChevronIcon orientation={isExpanded ? "down" : "right"} />}
              </button>
            </ArkMenu.Trigger>
          )}
          <ArkPortal>
            <ArkMenu.Positioner className="z-overlay!">
              <ArkMenu.Content asChild>
                <nav
                  className={twJoin(
                    "rounded-lg border border-border-default bg-background-default p-space-sm shadow-overlay focus-visible:outline-hidden",
                    "data-[state=open]:animate-in data-[state=open]:fade-in",
                    "data-[state=closed]:animate-out data-[state=closed]:fade-out",
                  )}
                >
                  <ArkMenu.ItemGroup className="flex flex-col gap-space-sm">
                    <ul className="flex w-full flex-col gap-space-sm">
                      {items.map((item) => (
                        <SidebarGroupedItemFloatingMenuItem
                          label={item.label}
                          to={item.to}
                          exact={item.exact}
                          data-testid={item["data-testid"]}
                          key={item.key}
                        />
                      ))}
                    </ul>
                  </ArkMenu.ItemGroup>
                </nav>
              </ArkMenu.Content>
            </ArkMenu.Positioner>
          </ArkPortal>
        </ArkMenu.Root>
        {/* Floating tooltip */}
        <AnimatePresence>
          {!isMenuVisible && isTooltipVisible && (
            <RuntimeFloatingPortal>
              <SidebarItemFloatingTooltip label={label} floating={panelProps} isVisible={isTooltipVisible} />
            </RuntimeFloatingPortal>
          )}
        </AnimatePresence>
      </li>
    );
  }

  return (
    <li className="w-full">
      <ArkCollapsible.Root open={isExpanded} onOpenChange={expandHandlers.toggle}>
        {linkedHeader ?? (
          <ArkCollapsible.Trigger asChild>
            <button
              type="button"
              className={twResolve(sidebarItemVariants({ isActive: isActive || isExpanded }), "gap-space-xs pr-0")}
              data-testid={props["data-testid"]}
            >
              <span className="flex min-w-0 items-center gap-space-sm">
                <span className="flex size-icon-inline shrink-0 items-center justify-center [&_svg]:size-icon-inline">
                  {icon}
                </span>
                <motion.span
                  className="block truncate font-medium"
                  variants={sidebarItemLabelAnimation}
                  initial={isDesktop ? "hidden" : "visible"}
                  animate="visible"
                  exit="hidden"
                >
                  {label}
                </motion.span>
              </span>
              {/* Chevron owns the right-edge spacing via its control-md box, matching the linked header */}
              <span className="flex w-control-md shrink-0 items-center justify-center">
                <ChevronIcon orientation={isExpanded ? "down" : "right"} />
              </span>
            </button>
          </ArkCollapsible.Trigger>
        )}
        <ArkCollapsible.Content className="overflow-hidden data-[state=closed]:animate-collapse-y data-[state=open]:animate-expand-y">
          <ul className="flex w-full flex-col gap-space-sm py-space-sm pl-space-xl">
            {items.map((item) => (
              <SidebarItem {...item} key={item.key} />
            ))}
          </ul>
        </ArkCollapsible.Content>
      </ArkCollapsible.Root>
    </li>
  );
}

interface SidebarGroupedItemMenuItemProps extends Pick<
  SidebarBaseItemType,
  "label" | "to" | "exact" | "data-testid" | "onClick"
> {}

function SidebarGroupedItemFloatingMenuItem({
  label,
  exact,
  to,
  onClick,
  "data-testid": dataTestId,
}: SidebarGroupedItemMenuItemProps): React.ReactNode {
  return (
    <ArkMenu.Item value={label} asChild>
      {/* Hide focus-visible styling, rely on `data-highlighted` */}
      <NavLink
        className={({ isActive }) =>
          twResolve(
            sidebarItemVariants({ isActive }),
            !isActive && "focus-visible:bg-transparent data-highlighted:bg-background-brand-light",
          )
        }
        tabIndex={-1}
        end={exact}
        data-testid={dataTestId}
        {...{ to, onClick }}
      >
        <span className="block whitespace-nowrap">{label}</span>
      </NavLink>
    </ArkMenu.Item>
  );
}
